import { createContext, Dispatch, SetStateAction } from 'react';
import { TreeControlState } from 'components/explorer';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import {
  EagerLoadTreeNodeDTO,
  FragmentLayerDTO,
  ProteinLayerDTO,
  ScreenDTO,
} from 'business/models/Models';
import { SeedLayerDTO } from 'business/models/CustomModels';

export const eagerLoadNodesList = [
  ETreeNodeTypes.Project,
  ETreeNodeTypes.ProjectFolder,
  ETreeNodeTypes.Projects,
  ETreeNodeTypes.ProjectsFolder,
  ETreeNodeTypes.ScreensFolder,
  ETreeNodeTypes.Screens,
  ETreeNodeTypes.Screen,
];

export const eagerLoadNodesExperimentOnlyList = [
  ETreeNodeTypes.FragmentScreen,
  ETreeNodeTypes.Protein,
  ETreeNodeTypes.Seed,
];

export const getLayersExplorerAllowedNodeTypes = (isScreenRoute: boolean): string[] =>
  isScreenRoute
    ? eagerLoadNodesList
    : [...eagerLoadNodesList, ...eagerLoadNodesExperimentOnlyList];

export const filterNodesByAllowedTypes = (nodes: any[], allowedTypes: string[]): any[] =>
  (nodes || [])
    .filter((n) => allowedTypes.includes(n.type))
    .map((n) => ({
      ...n,
      children: n.children
        ? filterNodesByAllowedTypes(n.children, allowedTypes)
        : n.children,
    }));

export const removeNodesById = (nodes: any[], idsToRemove: number[]): any[] =>
  (nodes || [])
    .filter((n) => !idsToRemove.includes(n.id))
    .map((n) => ({
      ...n,
      children: n.children ? removeNodesById(n.children, idsToRemove) : n.children,
    }));

export const renameNodeInTree = (nodes: any[], id: number, newName: string): any[] => {
  if (!nodes?.length) {
    return nodes;
  }
  return nodes.map((n) => {
    if (n.id === id) {
      return { ...n, name: newName };
    }
    if (n.children?.length) {
      return { ...n, children: renameNodeInTree(n.children, id, newName) };
    }
    return n;
  });
};

export const preserveExpandedState = (oldNodes: any[], newNodes: any[]): any[] => {
  const expandedIds = new Set<number>();
  const collect = (nodes: any[]) => {
    (nodes || []).forEach((n) => {
      if (n?.state?.expanded && n.id != null) {
        expandedIds.add(n.id);
      }
      if (n?.children?.length) {
        collect(n.children);
      }
    });
  };
  collect(oldNodes);
  if (!expandedIds.size) {
    return newNodes;
  }
  const apply = (nodes: any[]): any[] =>
    (nodes || []).map((n) => ({
      ...n,
      state: expandedIds.has(n.id) ? { ...n.state, expanded: true } : n.state,
      children: n.children ? apply(n.children) : n.children,
    }));
  return apply(newNodes);
};

export const collectNodeAndDescendantIds = (
  nodes: any[],
  idsToRemove: number[],
): number[] => {
  const removeSet = new Set(idsToRemove);
  const result = new Set<number>();

  const walk = (node: any, ancestorDeleted: boolean) => {
    if (!node) {
      return;
    }
    const isDeleted = ancestorDeleted || removeSet.has(node.id);
    if (isDeleted && node.id != null) {
      result.add(node.id);
    }
    if (Array.isArray(node.children)) {
      node.children.forEach((child) => walk(child, isDeleted));
    }
  };

  (nodes || []).forEach((node) => walk(node, false));
  return [...result];
};

export const expandNodesRecursively = (nodes, expand) => {
  return nodes.map((node) => ({
    ...node,
    state: {
      ...node.state,
      expanded: expand,
    },
    children: node.children ? expandNodesRecursively(node.children, expand) : [],
  }));
};

export const findNodeAndExpandParents = (nodes, targetId) => {
  let foundNode = null;

  const recurse = (currentNodes) => {
    return currentNodes.map((node) => {
      const updatedNode = { ...node };

      // if children exist, go deeper
      if (node.children && node.children.length > 0) {
        const resultChildren = recurse(node.children);

        // if any child was found and expanded → expand this node too
        const childContainsFound = resultChildren.some((c) => c.state?.expanded);

        if (childContainsFound) {
          updatedNode.state = { ...node.state, expanded: true };
        }

        updatedNode.children = resultChildren;
      }

      // if this is the found node
      if (node.id === targetId) {
        foundNode = updatedNode;
        updatedNode.state = { ...node.state, expanded: true };
      }

      return updatedNode;
    });
  };

  const updatedNodes = recurse(nodes);
  return { updatedNodes, foundNode };
};

export type LayersContainerContextType = {
  treeControlState: TreeControlState;
  setTreeControlState: Dispatch<SetStateAction<TreeControlState>>;
};

export const LayersContainerContext = createContext<
  LayersContainerContextType | undefined
>(undefined);

export const getTreeNodeIdsByType = (
  root: EagerLoadTreeNodeDTO[],
  type: ETreeNodeTypes,
): Set<number> => {
  const result = new Set<number>();

  const traverse = (n: EagerLoadTreeNodeDTO) => {
    if (!n) {
      return;
    }

    if (n.type === type) {
      const id = n.type === ETreeNodeTypes.Screen ? n.extra?.id : n.id;

      if (id != null) {
        result.add(Number(id));
      }
    }

    if (Array.isArray(n.children)) {
      n.children.forEach(traverse);
    }
  };

  (root || []).forEach(traverse);

  return result;
};

export const findTreeNodeById = (nodes: any[], id: number): any | undefined => {
  for (const n of nodes || []) {
    if (n?.id === id) {
      return n;
    }
    const found = findTreeNodeById(n.children, id);
    if (found) {
      return found;
    }
  }
  return undefined;
};

export const getScreenNamesByEntityId = (nodes: any[]): Map<number, string> => {
  const names = new Map<number, string>();
  const walk = (list: any[]) => {
    (list || []).forEach((n) => {
      if (n?.type === ETreeNodeTypes.Screen && n.extra?.id != null) {
        names.set(Number(n.extra.id), n.name);
      }
      if (n?.children?.length) {
        walk(n.children);
      }
    });
  };
  walk(nodes);
  return names;
};

export type UseLayerBroadcastReceiverProps = {
  treeNodes: EagerLoadTreeNodeDTO[];
  selectedScreen: ScreenDTO | undefined;
  setSelectedScreen: React.Dispatch<React.SetStateAction<ScreenDTO | undefined>>;
  selectedFragmentLayerDetail: FragmentLayerDTO | null;
  setSelectedFragmentLayerDetail: React.Dispatch<
    React.SetStateAction<FragmentLayerDTO | null>
  >;
  editSeedData: SeedLayerDTO | undefined;
  setEditSeedData: React.Dispatch<React.SetStateAction<SeedLayerDTO | undefined>>;
  selectedSeedName: string | undefined;
  setSelectedSeedName: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedProteinLayerDetail: ProteinLayerDTO | null;
  setSelectedProteinLayerDetail: React.Dispatch<
    React.SetStateAction<ProteinLayerDTO | null>
  >;
  onFragmentDeleted: () => void;
  onProteinDeleted: () => void;
  onSeedDeleted: () => void;
};
